import React from 'react';
import { Button, Spinner } from '../../../../components';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  CREATE_MITIGATOR,
  DELETE_MITIGATOR,
  EDIT_MITIGATOR,
} from '../../queries';
import { useMutation } from '@apollo/client';

interface MitigatorFormProps {
  onCancel: () => void;
  data?: any;
  edit?: boolean;
}

interface FormValues {
  vendorName: string;
  contactName: string;
  email: string;
  phone: string;
}

export function MitigatorForm({ onCancel, data, edit }: MitigatorFormProps) {
  const mutation = edit ? EDIT_MITIGATOR : CREATE_MITIGATOR;
  const [mitigator, { loading, error }] = useMutation(mutation, {
    refetchQueries: ['GetMitigator', 'GetMitigators'],
  });

  const [mitigatorRemove] = useMutation(DELETE_MITIGATOR, {
    refetchQueries: ['GetMitigator', 'GetMitigators'],
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  if (loading) return <Spinner />;
  if (error) return <>Error! ${error.message}</>;

  const onFinish: SubmitHandler<FormValues> = async (values) => {
    let response;
    try {
      if (edit) {
        response = await mitigator({
          variables: {
            data: {
              uuid: data.uuid,
              vendorName: values.vendorName,
              contactName: values.contactName,
              email: values.email,
              phone: values.phone,
            },
          },
        });
      } else {
        response = await mitigator({
          variables: {
            data: {
              vendorName: values.vendorName,
              contactName: values.contactName,
              email: values.email,
              phone: values.phone,
            },
          },
        });
      }

      if (response.data) {
        console.log('Mutation successful', response.data);
        onCancel();
      }
    } catch (e) {
      console.log('Error', e);
    }
  };

  const handleDelete = async () => {
    let response;
    try {
      response = await mitigatorRemove({
        variables: {
          uuid: data.uuid,
        },
      });

      if (response.data) {
        console.log('Mutation successful', response.data);
        onCancel();
      }
    } catch (e) {
      console.log('Error', e);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onFinish)}
      className="  w-full flex flex-col flex-nowrap mt-8"
    >
      <div className="inline-flex flex-row flex-nowrap items-stcenter my-2 justify-between">
        <label
          className="font-semibold text-primary-200 text-sm"
          htmlFor="vendorName"
        >
          Vendor
        </label>
        <input
          placeholder="Vendor Name"
          className="w-64 border rounded-lg text-left text-primary-200 font-normal text-sm px-2 py-1"
          type="text"
          id="vendorName"
          defaultValue={edit ? data?.vendorName : ''}
          {...register('vendorName', { required: true })}
        />
        {errors.vendorName && <span>This field is required</span>}
      </div>
      <div className="inline-flex flex-row flex-nowrap items-stcenter my-2 justify-between">
        <label
          className="font-semibold text-primary-200 text-sm"
          htmlFor="contactName"
        >
          Contact Name
        </label>
        <input
          placeholder="Contact Name"
          className="w-64 border rounded-lg text-left text-primary-200 font-normal text-sm px-2 py-1"
          type="text"
          id="contactName"
          defaultValue={edit ? data?.contactName : ''}
          {...register('contactName', { required: true })}
        />
        {errors.contactName && <span>This field is required</span>}
      </div>
      <div className="inline-flex flex-row flex-nowrap items-center my-2 justify-between">
        <label
          className="font-semibold text-primary-200 text-sm"
          htmlFor="email"
        >
          Email
        </label>
        <input
          placeholder="example.example@abc.com"
          className="w-64 border rounded-lg text-left text-primary-200 font-normal text-sm px-2 py-1"
          type="email"
          id="email"
          defaultValue={edit ? data?.email : ''}
          {...register('email', { required: true })}
        />
        {errors.email && <span>This field is required</span>}
      </div>
      <div className="inline-flex flex-row flex-nowrap items-center my-2 justify-between">
        <label
          className="font-semibold text-primary-200 text-sm"
          htmlFor="phone"
        >
          Phone
        </label>
        <input
          placeholder="+1 123 456 7891"
          className="w-64 border rounded-lg text-left text-primary-200 font-normal text-sm px-2 py-1"
          type="text"
          id="phone"
          defaultValue={edit ? data?.phone : ''}
          {...register('phone', { required: true })}
        />
        {errors.phone && <span>This field is required</span>}
      </div>
      <div className="inline-flex flex-row justify-between mt-6">
        <Button type={'link'} htmlType={'button'} onClick={handleDelete}>
          Delete Entry
        </Button>
        <Button
          type="primary"
          className="text-sm min-w-[180px]"
          htmlType={'submit'}
        >
          Save
        </Button>
      </div>
    </form>
  );
}
